import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiStar, FiTrendingUp, FiQuote } = FiIcons;

const TestimonialSection = () => {
  const testimonials = [
    {
      name: "Sarah Chen",
      store: "Chen's Fashion Boutique",
      revenue: "$8,400",
      before: "12 abandoned carts/day",
      after: "87% recovery rate",
      avatar: "SC",
      quote: "I was skeptical about spending $250, but this paid for itself in 3 days. The AI recovered $2,400 in the first week alone. It's like having a 24/7 sales team that never sleeps.",
      gradient: "from-purple-400 to-pink-400"
    },
    {
      name: "Marcus Rodriguez",
      store: "Rodriguez Electronics",
      revenue: "$12,600",
      before: "20 abandoned carts/day",
      after: "92% recovery rate",
      avatar: "MR",
      quote: "Our biggest problem was international customers abandoning carts at 3 AM. Now the AI handles them perfectly. We're seeing an extra $12K per month we would have never had.",
      gradient: "from-blue-400 to-purple-400"
    },
    {
      name: "Emily Thompson",
      store: "Thompson's Home Goods",
      revenue: "$6,200",
      before: "8 abandoned carts/day",
      after: "79% recovery rate",
      avatar: "ET",
      quote: "The best investment we've made this year. The AI answers questions better than our human team did, and it's saving us 15 hours per week in customer service time.",
      gradient: "from-emerald-400 to-teal-400"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full font-bold mb-6">
            <SafeIcon icon={FiTrendingUp} className="text-emerald-600" />
            REAL RESULTS FROM REAL STORE OWNERS
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Store Owners Like You Are{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">
              Making Thousands Extra
            </span>{' '}
            Every Month
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-medium">
            Don't take our word for it. See what happens when real stores implement our AI Sales Assistant.
          </p>
        </motion.div>

        {/* Featured Testimonial */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 md:p-12 text-white mb-12 relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]"></div>
          </div>
          
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <SafeIcon icon={FiQuote} className="text-6xl mb-6 opacity-50" />
              <blockquote className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
                "This AI recovered $8,400 in our first month. We were literally throwing money away before this."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                  SC
                </div>
                <div>
                  <div className="text-xl font-bold">Sarah Chen</div>
                  <div className="text-emerald-100">Chen's Fashion Boutique</div>
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="text-yellow-300 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4">Sarah's Results:</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Before (Monthly Lost):</span>
                  <span className="text-red-300 font-bold">$5,200</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>After (Recovered):</span>
                  <span className="text-emerald-300 font-bold">$8,400</span>
                </div>
                <div className="h-px bg-white/20 my-3"></div>
                <div className="flex justify-between items-center">
                  <span className="font-bold">Net Profit:</span>
                  <span className="text-2xl font-black text-emerald-300">+$8,150</span>
                </div>
                <div className="text-center text-sm text-emerald-100 mt-3">
                  ROI: 3,260% in the first month
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Other Testimonials */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
          {testimonials.slice(1).map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current" />
                ))}
              </div>
              
              <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 font-medium">
                "{testimonial.quote}"
              </blockquote>
              
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center text-white font-bold`}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.store}</div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-black text-emerald-600">{testimonial.revenue}</div>
                  <div className="text-xs text-gray-500">Monthly Recovery</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-gray-900 to-black rounded-3xl p-8 text-white text-center"
        >
          <h3 className="text-2xl font-bold mb-8">Combined Impact Across All Clients:</h3>
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-4xl font-black text-emerald-400 mb-2">$847K</div>
              <p className="text-gray-400">Revenue Recovered</p>
            </div>
            <div>
              <div className="text-4xl font-black text-blue-400 mb-2">87%</div>
              <p className="text-gray-400">Average Recovery Rate</p>
            </div>
            <div>
              <div className="text-4xl font-black text-purple-400 mb-2">3.2x</div>
              <p className="text-gray-400">ROI in First Month</p>
            </div>
            <div>
              <div className="text-4xl font-black text-orange-400 mb-2">237</div>
              <p className="text-gray-400">Happy Store Owners</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialSection;